import type {Call,CallOutcome,CallQuery} from '@bcio-voice/contracts';
import {useCallback,useEffect,useRef,useState} from 'react';
import {type ApiClient,type CollectionEnvelope,isAbortError,query} from '../../lib/api.ts';
import {describeError,type ErrorView} from '../../lib/errorCopy.ts';

export interface HistoryFilter {
 from?:string|undefined;to?:string|undefined;user_id?:string|undefined;number_id?:string|undefined;team_id?:string|undefined;phone_number?:string|undefined;
 final_only?:boolean|undefined;outcome_group?:'other'|'eligible'|'unanswered'|'not_connected'|undefined;duration?:'talk'|'hold'|'queue'|undefined;person_scope?:'activity'|'handled'|undefined;
 direction:''|Call['direction'];
 state:''|Call['state'];
 /** Omitted by older workspace hosts; empty and omitted both mean all outcomes. */
 outcome?:''|CallOutcome;
}
export const emptyHistoryFilter=():HistoryFilter=>({direction:'',state:'',outcome:''});
export interface HistoryState {
 ids:string[];nextCursor:string|null;loading:boolean;error:ErrorView|null;filter:HistoryFilter;loaded:boolean;
}

/** Apply server fields only: optimistic drop receipts never imply a delivered outcome. */
export function matchesHistory(call:Call,f:HistoryFilter):boolean {
 const final=call.state==='ended'||call.state==='failed';
 return (!f.direction||call.direction===f.direction)&&(!f.state||call.state===f.state)
  &&(!f.outcome||call.outcome===f.outcome)&&(!f.final_only||final)
  &&(!f.outcome_group||(f.outcome_group==='other'?!(final&&['answered','missed','failed'].includes(call.outcome??'')):f.outcome_group==='eligible'?final&&!!call.outcome&&call.outcome!=='cancelled':f.outcome_group==='not_connected'?final&&call.answered_at===null:final&&call.outcome!=='answered'))
  &&(!f.duration||call[({talk:'talk_ms',hold:'hold_ms',queue:'queue_ms'} as const)[f.duration]]>0);
 // Date, person/team/line and external-number scope is authoritative on the server, including pagination.
 // Public Call values intentionally omit some actor/routing identities; do not approximate those filters here.
}

/** Shared by the workspace and real React tests. Ref state changes synchronously so
 * event refreshes and two actions in one render always use the current filter/cursor. */
export function useCallHistory(api:ApiClient,options:{onCalls:(calls:Call[])=>void;onPage:(env:CollectionEnvelope<Call>)=>void;onError?:(error:unknown)=>void}) {
 const [history,setHistory]=useState<HistoryState>(()=>({ids:[],nextCursor:null,loading:true,error:null,filter:emptyHistoryFilter(),loaded:false}));
 const current=useRef(history),callbacks=useRef(options),request=useRef(0),controller=useRef<AbortController|null>(null);
 callbacks.current=options;
 const publish=useCallback((value:HistoryState)=>{current.current=value;setHistory(value);},[]);
 const fetchPage=useCallback((filter:HistoryFilter|undefined,more:boolean)=>{
  const before=current.current;
  if(more&&(!before.nextCursor||before.loading))return;
  const id=++request.current;
  controller.current?.abort();
  const abort=new AbortController();controller.current=abort;
  const f={...(filter??before.filter)};
  // A new filter starts clean. A reload of a list already on screen keeps the last error until it
  // settles, so a failing refresh repeated by every call event can't blink the error in and out.
  publish({...before,filter:f,loading:true,error:!filter&&before.loaded?before.error:null,...(filter?{ids:[],nextCursor:null,loaded:false}:{})});
  const cursor=more?before.nextCursor:null;
  void api.request<CollectionEnvelope<Call>>('/calls'+query({limit:50,...f,cursor}),{signal:abort.signal}).then(env=>{
   if(id!==request.current)return;
   callbacks.current.onCalls(env.data);
   const ids=more?[...current.current.ids,...env.data.map(c=>c.id)]:env.data.map(c=>c.id);
   publish({ids:[...new Set(ids)],nextCursor:env.page.next_cursor,loading:false,error:null,filter:f,loaded:true});
   callbacks.current.onPage(env);
  },error=>{
   if(id!==request.current||isAbortError(error))return;
   publish({...current.current,loading:false,error:describeError(error,more?"Couldn't load more calls":"Couldn't load calls")});
   callbacks.current.onError?.(error);
  });
 },[api,publish]);
 const loadHistory=useCallback((filter?:HistoryFilter)=>fetchPage(filter,false),[fetchPage]);
 const loadMoreHistory=useCallback(()=>fetchPage(undefined,true),[fetchPage]);
 useEffect(()=>()=>{++request.current;controller.current?.abort();},[]);
 return {history,loadHistory,loadMoreHistory};
}
